import { logger } from "@/lib/logger";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ProtocolFeeSchedule {
  id: string;
  trial_id: string;
  fee_category: string;
  fee_name: string;
  fee_description?: string;
  amount: number;
  currency: string;
  effective_from: string;
  effective_to?: string;
  is_active: boolean;
  event_trigger_type: string;
  created_at: string;
  updated_at: string;
}

export function useProtocolFeeSchedules(trialId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['protocol-fee-schedules', trialId],
    queryFn: async () => {
      let query = (supabase as any)
        .from('protocol_fee_schedules')
        .select('*')
        .order('created_at', { ascending: false });

      if (trialId) {
        query = query.eq('trial_id', trialId);
      }

      const { data, error } = await query;
      
      if (error) {
        logger.error('Error fetching protocol fee schedules:', error);
        throw error;
      }

      return data as ProtocolFeeSchedule[];
    },
    enabled: true,
    staleTime: 60 * 1000, // 1 minute
  });

  const createMutation = useMutation({
    mutationFn: async (newSchedule: Omit<ProtocolFeeSchedule, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await (supabase as any)
        .from('protocol_fee_schedules')
        .insert(newSchedule)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol-fee-schedules'] });
      toast({
        title: "Success",
        description: "Fee schedule created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create fee schedule",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ProtocolFeeSchedule> }) => {
      const { data, error } = await (supabase as any)
        .from('protocol_fee_schedules')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol-fee-schedules'] });
      toast({
        title: "Success",
        description: "Fee schedule updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update fee schedule",
        variant: "destructive",
      });
    },
  });

  return {
    ...query,
    createFeeSchedule: createMutation.mutate,
    updateFeeSchedule: updateMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
